import React from 'react';
import { Breadcrumbs, Anchor } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';

// Tipos para el breadcrumb
export type Crumb = {
  label: string;
  to?: string; // Si no hay to, es el item actual
};

export type AfaBreadcrumbsProps = {
  items: Crumb[];
  separatorIcon?: React.ElementType;
  size?: 'xs' | 'sm' | 'md';
  fw?: number;
  collapseOnMobile?: boolean;
  className?: string;
  style?: React.CSSProperties;
  styles?: any;
  onNavigate?: (to: string) => boolean | Promise<boolean>; // Callback que retorna true para permitir navegación, false para cancelarla
};

/**
 * Wrapper del Breadcrumbs de Mantine con defaults de AFA y integración con React Router
 * 
 * Ejemplos de uso:
 * 
 * // Separador default + todos visíbles
 * <AfaBreadcrumbs
 *   items={[
 *     { label: 'Inicio', to: '/' },
 *     { label: 'Documentos', to: '/docs' },
 *     { label: 'Breadcrumbs' }, // actual
 *   ]}
 * />
 * 
 * // Separador custom + colapso en mobile
 * <AfaBreadcrumbs
 *   separatorIcon={IconChevronRight}
 *   collapseOnMobile
 *   items={[
 *     { label: 'Inicio', to: '/' },
 *     { label: 'Docs', to: '/docs' },
 *     { label: 'Components', to: '/docs/components' },
 *     { label: 'Breadcrumbs' }, // actual
 *   ]}
 * />
 */
export function AfaBreadcrumbs({
  items,
  separatorIcon = IconChevronRight,
  size = 'xs',
  fw = 600,
  collapseOnMobile = false,
  className,
  style,
  styles,
  onNavigate,
}: AfaBreadcrumbsProps) {
  const navigate = useNavigate();
  // Determinar si necesitamos colapsar en mobile
  const shouldCollapse = collapseOnMobile && items.length > 3;
  
  // Preparar items para renderizar
  let itemsToRender = items;
  
  if (shouldCollapse) {
    // En mobile: [primero] … [último]
    const first = items[0];
    const last = items[items.length - 1];
    const ellipsis = { label: '…', isEllipsis: true };
    
    itemsToRender = [first, ellipsis as any, last];
  }

  // Crear el separador
  const separator = React.createElement(separatorIcon, { size: 14 });

  // Renderizar items
  const breadcrumbItems = itemsToRender.map((item, index) => {
    const isLast = index === itemsToRender.length - 1;
    const hasLink = item.to && !isLast;
    
    // Item especial para ellipsis
    if ((item as any).isEllipsis) {
      return (
        <span 
          key="ellipsis"
          aria-hidden="true" 
          style={{ 
            color: 'var(--mantine-color-neutral-6)',
            fontSize: `var(--mantine-font-size-${size})`,
            fontWeight: fw,
          }}
        >
          …
        </span>
      );
    }
    
    // Item normal con link
    if (hasLink) {
      if (onNavigate) {
        // Si hay callback de navegación, usar onClick personalizado
        return (
          <Anchor
            key={`${item.label}-${index}`}
            c="var(--mantine-color-brand-6)"
            underline="hover"
            size={size}
            fw={fw}
            style={{ cursor: 'pointer' }}
            onClick={async (e) => {
              e.preventDefault();
              const canNavigate = await onNavigate(item.to!);
              if (canNavigate) {
                navigate(item.to!);
              }
            }}
          >
            {item.label}
          </Anchor>
        );
      } else {
        // Comportamiento normal con Link
        return (
          <Anchor
            key={`${item.label}-${index}`}
            component={Link}
            to={item.to!}
            c="var(--mantine-color-brand-6)"
            underline="hover"
            size={size}
            fw={fw}
          >
            {item.label}
          </Anchor>
        );
      }
    }
    
    // Último item (actual) sin link
    return (
      <span
        key={`${item.label}-${index}`}
        aria-current="page"
        style={{ 
          color: 'var(--mantine-color-neutral-6)',
          fontSize: `var(--mantine-font-size-${size})`,
          fontWeight: fw,
        }}
      >
        {item.label}
      </span>
    );
  });

  return (
    <div
      style={{
        fontSize: `var(--mantine-font-size-${size})`,
        fontWeight: fw,
        ...style,
      }}
    >
      <Breadcrumbs
        separator={separator}
        styles={{
          root: {
            '--mantine-spacing-xs': '4px',
            margin: '0',
            padding: '0',
            fontSize: `var(--mantine-font-size-${size})`,
            fontWeight: fw,
          },
          separator: {
            marginInline: '4px !important',
          },
          breadcrumb: {
            fontSize: `var(--mantine-font-size-${size})`,
            fontWeight: fw,
          },
          ...styles,
        }}
        {...(className && { className })}
      >
        {breadcrumbItems}
      </Breadcrumbs>
    </div>
  );
}